import { useEffect, useMemo, useState } from 'react';
import { Checkbox, Select, Skeleton, Spin, message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';

import NoData from 'components/NoData';
import useTranslation from 'customHooks/translate';
import { ERefetchType } from 'enums';
import { SUB_USER_PACKAGE_PLANS, SUB_USERS_BY_ACCOUNT_ID } from 'events/types';
import {
  checkout,
  getPackagePlansForSubUsers,
  getUserSubscriptionPackagePlanAmount,
  SubUserPackagePlan,
  SubUserSubscriptionPlanRequest,
} from 'services/payment';
import { getSubUserListByAccountId, SubUserListItem } from 'services/sub-users';
import { toastError } from 'services/helpers/getError';
import { filterPaidPackagePlans, openCheckoutPaymentInNewTab } from 'utils';
import { SubUserSelection } from 'types';
import {
  DEFAULT_PAYMENT_CURRENCY_CODE,
  DEFAULT_PAYMENT_CURRENCY_TEXT,
} from 'consts';

const SubUserPricingSection = () => {
  const translation = useTranslation();
  const [selections, setSelections] = useState<
    Record<number, SubUserSelection>
  >({});
  const [totalAmount, setTotalAmount] = useState<number | null>(null);
  const [totalCurrency, setTotalCurrency] = useState<string | null>(null);

  const subUsersQuery = useQuery<SubUserListItem[]>({
    queryKey: [SUB_USERS_BY_ACCOUNT_ID],
    queryFn: async () => {
      const response = await getSubUserListByAccountId();
      return response.data.data ?? [];
    },
  });

  const plansQuery = useQuery<SubUserPackagePlan[]>({
    queryKey: [SUB_USER_PACKAGE_PLANS],
    queryFn: async () => {
      const response = await getPackagePlansForSubUsers();
      return response.data.data ?? [];
    },
    refetchOnMount: ERefetchType.ALWAYS,
    staleTime: 0,
  });

  const subUsers = subUsersQuery.data ?? [];

  const showUpgradeNotice = useMemo(
    () => subUsers?.some((user) => user?.isUpgradeEnabled === false),
    [subUsers]
  );

  const paidPlans = useMemo(
    () => filterPaidPackagePlans(plansQuery.data ?? []),
    [plansQuery.data]
  );

  useEffect(() => {
    if (!subUsers.length || !paidPlans.length) return;

    const paidPlanIds = new Set(paidPlans.map((plan) => plan.id));

    setSelections((prev) => {
      let hasChanges = false;
      const next = { ...prev };

      subUsers.forEach((user) => {
        const activePlanId = user?.activeSubscriptionPackageId;
        if (activePlanId == null || !paidPlanIds.has(activePlanId)) return;
        if (prev[user.userId]?.planId != null) return;

        next[user.userId] = {
          checked: prev[user.userId]?.checked ?? false,
          planId: activePlanId,
        };
        hasChanges = true;
      });

      return hasChanges ? next : prev;
    });
  }, [subUsers, paidPlans]);

  const planOptions = useMemo(
    () =>
      paidPlans.map((plan) => ({
        value: plan.id,
        label: `${plan.packagePeriodInMonth} - ${plan.price} ${plan.packageCurrencyId}`,
      })),
    [paidPlans]
  );

  const selectedSubscriptions = useMemo<SubUserSubscriptionPlanRequest[]>(
    () =>
      subUsers.flatMap((user) => {
        const selection = selections[user.userId];

        if (!selection?.checked || selection.planId === null) {
          return [];
        }

        return [
          {
            userId: String(user.userId),
            packagePlanId: selection.planId,
          },
        ];
      }),
    [subUsers, selections]
  );

  const calculateMutation = useMutation(getUserSubscriptionPackagePlanAmount, {
    onSuccess: ({ data }) => {
      const result = data.data;

      if (typeof result === 'number') {
        setTotalAmount(result);
        setTotalCurrency(null);
        return;
      }

      setTotalAmount(result?.totalAmount ?? result?.amount ?? null);
      setTotalCurrency(result?.packageCurrencyId ?? result?.currency ?? null);
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: checkout,
    onSuccess: (response) => {
      if (!openCheckoutPaymentInNewTab(response)) {
        message.error(translation.profile.paymentPageOpenFailed);
      }
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const isCheckoutReady =
    totalAmount != null && selectedSubscriptions.length > 0;

  const getSelection = (userId: number): SubUserSelection =>
    selections[userId] ?? {
      checked: false,
      planId: null,
    };

  const handleCheckboxChange = (user: SubUserListItem, checked: boolean) => {
    setTotalAmount(null);
    setTotalCurrency(null);
    setSelections((prev) => ({
      ...prev,
      [user.userId]: {
        checked,
        planId: prev[user.userId]?.planId ?? null,
      },
    }));
  };

  const handlePlanChange = (userId: number, planId: number) => {
    setTotalAmount(null);
    setTotalCurrency(null);
    setSelections((prev) => ({
      ...prev,
      [userId]: {
        checked: prev[userId]?.checked ?? false,
        planId,
      },
    }));
  };

  const handleCalculate = () => {
    if (!selectedSubscriptions.length) {
      message.warning(translation.profile.selectUsersAndPlans);
      return;
    }

    setTotalAmount(null);
    setTotalCurrency(null);
    calculateMutation.mutate(selectedSubscriptions);
  };

  const handleCheckout = () => {
    if (!selectedSubscriptions.length) {
      message.warning(translation.profile.selectUsersAndPlans);
      return;
    }

    if (totalAmount == null) {
      message.warning(translation.profile.calculateBeforePay);
      return;
    }

    checkoutMutation.mutate({
      amount: totalAmount,
      currencyCode: DEFAULT_PAYMENT_CURRENCY_CODE,
      currencyText: DEFAULT_PAYMENT_CURRENCY_TEXT,
      userSubscriptionDetails: selectedSubscriptions,
    });
  };

  if (subUsersQuery.isLoading || plansQuery.isLoading) {
    return (
      <div className="grid grid-cols-1 gap-[12px] md:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} active paragraph={{ rows: 2 }} />
        ))}
      </div>
    );
  }

  if (!subUsers.length) {
    return <NoData />;
  }

  return (
    <Spin spinning={calculateMutation.isLoading || checkoutMutation.isLoading}>
      <div className="flex min-h-[120px] flex-col gap-[16px]">
        {showUpgradeNotice && (
          <div className="rounded-[12px] border border-[rgba(70,106,251,0.2)] bg-[rgba(70,106,251,0.06)] px-[16px] py-[12px] text-[14px] leading-relaxed text-[rgba(41,45,50,0.85)]">
            {translation.profile.subUserUpgradeNotAllowed}
          </div>
        )}

        <div className="grid grid-cols-1 gap-[12px] md:grid-cols-2">
          {subUsers.map((user) => {
            const selection = getSelection(user.userId);
            const expiryDate = user?.subscriptionExpiryDate;

            return (
              <div
                key={user.userId}
                className="flex flex-col gap-[12px] rounded-[12px] border border-[rgba(41,45,50,0.12)] bg-white p-[16px]"
              >
                <label className="flex min-w-0 cursor-pointer items-start gap-[12px]">
                  {user?.isUpgradeEnabled && (
                    <Checkbox
                      checked={selection.checked}
                      className="mt-[3px]"
                      onChange={(event) =>
                        handleCheckboxChange(user, event.target.checked)
                      }
                    />
                  )}
                  <div className="flex min-w-0 flex-col gap-[6px]">
                    <span className="truncate text-[15px] font-medium text-[#1D1D1D] md:text-[16px]">
                      {user.fullName}
                    </span>
                    {expiryDate && (
                      <span className="inline-flex w-fit items-center rounded-[6px] bg-[rgba(70,106,251,0.08)] px-[10px] py-[4px] text-[12px] text-[rgba(41,45,50,0.75)]">
                        {translation.profile.subscriptionExpiryDate}:{' '}
                        <span className="ml-[4px] font-semibold tabular-nums text-[#466AFB]">
                          {expiryDate}
                        </span>
                      </span>
                    )}
                  </div>
                </label>

                {user?.isUpgradeEnabled && (
                  <Select
                    value={selection.planId ?? undefined}
                    options={planOptions}
                    disabled={!selection.checked}
                    placeholder={translation.profile.selectPackage}
                    onChange={(planId) => handlePlanChange(user.userId, planId)}
                    className="w-full"
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-col items-start gap-[12px] sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-[12px]">
            <button
              type="button"
              onClick={handleCalculate}
              disabled={
                calculateMutation.isLoading || checkoutMutation.isLoading
              }
              className="rounded-[8px] border border-[#466AFB] bg-white px-[24px] py-[10px] text-[14px] font-semibold text-[#466AFB] transition-all hover:bg-[rgba(70,106,251,0.06)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {translation.profile.calculate}
            </button>
            <button
              type="button"
              onClick={handleCheckout}
              disabled={
                !isCheckoutReady ||
                calculateMutation.isLoading ||
                checkoutMutation.isLoading
              }
              className="rounded-[8px] bg-green-600 px-[24px] py-[10px] text-[14px] font-semibold text-white transition-all hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {translation.profile.pay}
            </button>
          </div>
          {totalAmount != null && (
            <p className="text-[16px] font-semibold text-[#1D1D1D] md:text-[18px]">
              {translation.profile.totalAmount}:{' '}
              <span className="tabular-nums text-[#466AFB]">
                {totalAmount}
                {totalCurrency ? ` ${totalCurrency}` : ' AZN'}
              </span>
            </p>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default SubUserPricingSection;
